import {
  EditedWatchlistGroup,
  InstrumentStoreItem,
  InstrumentSubscriptionDataScope,
  ListedInstrument,
  WatchListColumn,
  WatchListGroup,
  WatchListGroupId,
  WatchListGroupTab,
  WatchListInstrument,
  WatchListInstrumentFull,
  WatchlistResource,
  checkAndValidateUniquePropertyValueInObjArray,
  getUnknownListedInstrument,
  groupByFlat,
  mergeObjectInto,
  uniqueArray,
  watchListDefaultsAtom,
  watchListGroupsAtom,
} from '@eih-netbroker/common';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { useLocale } from '../../../context/NbLocalizationContext';
import { NbDataGridRef } from '../../../shared/components/data-grid/DataGrid';
import { useEStream } from '../../../shared/services/sse/useEStream';
import { useInstrumentSearch } from '../../../shared/services/useInstrumentSearch';
import { useResource, useResourceHeaders } from '../../../shared/services/useResource';
import { store } from '../../../shared/state/store';
import { useLogger } from '../../../shared/utils/useLogger';

const defaultWatchListColumns: Array<keyof WatchListInstrument> = [
  'instrument',
  'lastPrice',
  'change',
  'changePercent',
  'bidPrice',
  'askPrice',
  'low',
  'high',
  'averagePrice',
  'volumeQuantity',
  'volumeValue',
  'annualLow',
  'annualHigh',
];
const watchListInstrumentFields: Array<keyof WatchListInstrument> = [
  ...defaultWatchListColumns,
  'name',
  'market',
  'currency',
  'strikePrice',
  'strikeBarrier',
];
export const allColumns: Array<WatchListColumn> = watchListInstrumentFields.map(field => ({ field, selected: true }));

export const defaultColumns = watchListInstrumentFields.map(field => ({
  field,
  selected: defaultWatchListColumns.includes(field),
}));

const updateSingleWatchListInstrument = (
  watchListInstrument: WatchListInstrumentFull,
  instrumentStoreItem: InstrumentStoreItem,
) => {
  // update the watchlist instrument item
  watchListInstrument.instrumentStoreItem = structuredClone(instrumentStoreItem);
  watchListInstrument.closePrice = instrumentStoreItem.closePrice;
  watchListInstrument.lastPrice = instrumentStoreItem.price?.value;
  watchListInstrument.bidPrice = instrumentStoreItem.bestBid;
  watchListInstrument.askPrice = instrumentStoreItem.bestAsk;

  if (instrumentStoreItem.details) {
    watchListInstrument.low = instrumentStoreItem.details.lowDaily;
    watchListInstrument.high = instrumentStoreItem.details.highDaily;
    watchListInstrument.averagePrice = instrumentStoreItem.details.averagePrice;
    watchListInstrument.volumeQuantity = instrumentStoreItem.details.volumeQuantity;
    watchListInstrument.volumeValue = instrumentStoreItem.details.volumeNominal;
    watchListInstrument.annualLow = instrumentStoreItem.details.low52Week;
    watchListInstrument.annualHigh = instrumentStoreItem.details.high52Week;
  }
};

export const useWatchList = () => {
  const logger = useLogger('useWatchList');
  const [watchListGroups, setWatchListGroups] = useAtom(watchListGroupsAtom);

  const {
    getWatchlist,
    updateWatchListGroup,
    deleteWatchListGroup: deleteGroup,
    updateWatchListGroupOrder,
    createWatchListGroup,
    getWatchlistDefaults,
    getWatchlistDefaultGroup,
  } = useResource(WatchlistResource);
  const { getListedInstrumentList } = useInstrumentSearch();
  const { getResourceHeaderWithToken } = useResourceHeaders();
  const { translation } = useLocale();
  const { subscribeToInstrumentChanges, getInstrumentStoreItem } = useEStream();
  const [defaults, setDefaults] = useAtom(watchListDefaultsAtom);

  const mapWatchListGroupToWatchListGroupRequest = (watchListGroups: WatchListGroup) => {
    return {
      watchlistGroupId: watchListGroups.id,
      watchListGroupTab: {
        ...watchListGroups,
        columns: watchListGroups.columns.map(c => ({ column: c.field, selected: c.selected })),
        items: watchListGroups.items.map(item => ({ ric: item.instrument.ric })),
        id: watchListGroups.id,
      },
    };
  };

  const mapWatchListGroupTabToWatchListGroup = useCallback(
    async (group: WatchListGroupTab) => {
      const ricList = group.items?.map(i => i.ric) ?? [];
      const uniqueRicList = uniqueArray(ricList);
      const instrumentLists = await getListedInstrumentList(uniqueRicList);

      return {
        ...group,
        id: group.id + '',
        name: group.name,
        columns:
          group.columns?.map(c => ({ field: c.column as keyof WatchListInstrument, selected: c.selected })) ??
          defaultColumns,
        items: group.items?.map<WatchListInstrumentFull>(item => ({
          ...item,
          id: item.ric,
          instrument: instrumentLists?.find(instrument => instrument.ric === item.ric)!,
        })) as WatchListInstrumentFull[],
      };
    },
    [getListedInstrumentList],
  );

  const loadWatchListGroups = useCallback(async () => {
    setWatchListGroups({ isLoading: true, error: false, watchListGroups: [] });
    try {
      const response = await getWatchlist(getResourceHeaderWithToken());
      const ricList = response.data.groups!.reduce<string[]>(
        (acc, currentValue) => [...(currentValue.items?.map(i => i.ric) ?? []), ...acc],
        [],
      );
      const uniqueRicList = uniqueArray(ricList);
      const instrumentMap = groupByFlat<ListedInstrument>(await getListedInstrumentList(uniqueRicList), 'ric');

      const watchlistGroupsWithInstruments = response.data.groups?.map<WatchListGroup>(group => ({
        ...group,
        id: group.id,
        name: group.name,
        columns: group.columns.map(c => ({ field: c.column as keyof WatchListInstrument, selected: c.selected })),
        items: group.items?.map<WatchListInstrumentFull>(item => {
          const instrument =
            instrumentMap[item.ric] || getUnknownListedInstrument(item.ric, translation.instrument.unknown);
          const res = {
            ...item,
            id: item.ric,
            instrument,
            instrumentStoreItem: getInstrumentStoreItem(instrument),
          } as WatchListInstrumentFull;
          if (res.instrumentStoreItem) {
            updateSingleWatchListInstrument(res, res.instrumentStoreItem);
          }
          return res;
        }) as WatchListInstrumentFull[],
      }));
      setWatchListGroups({ isLoading: false, error: false, watchListGroups: watchlistGroupsWithInstruments });
    } catch (e) {
      setWatchListGroups({ isLoading: false, error: true, watchListGroups: [] });
    }
  }, [
    getWatchlist,
    getResourceHeaderWithToken,
    getListedInstrumentList,
    setWatchListGroups,
    translation,
    getInstrumentStoreItem,
  ]);

  /**
   * Creates a new WatchList group
   * @param watchListGroup
   */
  const createOrUpdateWatchListGroup = useCallback(
    async (watchListGroup: EditedWatchlistGroup): Promise<number> => {
      const groupId = watchListGroup.id;
      const isCreating = !groupId;
      const currentWatchlistGroupId = isCreating ? Date.now() : groupId;

      const trimmedName = watchListGroup.name.trim();
      const uniqueName = checkAndValidateUniquePropertyValueInObjArray(
        watchListGroups.watchListGroups,
        trimmedName,
        currentWatchlistGroupId,
        o => o.name,
        o => o.id,
      );
      const updatedWatchListGroup: WatchListGroup = {
        ...watchListGroup,
        id: currentWatchlistGroupId,
        name: uniqueName,
      };

      const newWatchListGroups: Array<WatchListGroup> = watchListGroups.watchListGroups.map(group =>
        group.id === updatedWatchListGroup.id ? updatedWatchListGroup : group,
      );
      if (isCreating) {
        const createWatchListResponse = await createWatchListGroup(
          {
            postWatchlistGroupRequest:
              mapWatchListGroupToWatchListGroupRequest(updatedWatchListGroup).watchListGroupTab,
          },
          getResourceHeaderWithToken(),
        );

        updatedWatchListGroup.id = Number(createWatchListResponse.data);
        newWatchListGroups.push(updatedWatchListGroup);
      } else {
        await updateWatchListGroup(
          mapWatchListGroupToWatchListGroupRequest(updatedWatchListGroup),
          getResourceHeaderWithToken(),
        );
      }

      setWatchListGroups(curr => mergeObjectInto({ ...curr }, { watchListGroups: newWatchListGroups }));

      return updatedWatchListGroup.id;
    },
    [setWatchListGroups, watchListGroups, getResourceHeaderWithToken, updateWatchListGroup, createWatchListGroup],
  );

  /**
   * Deletes a WatchList group
   * @param watchListGroupId
   */
  const deleteWatchListGroup = useCallback(
    async (watchListGroupId: number): Promise<void> => {
      const newWatchListGroups: Array<WatchListGroup> = watchListGroups.watchListGroups.filter(
        group => group.id !== watchListGroupId,
      );
      // UI trick, first save the new list, and then update the local state, so during loading it will look better

      await deleteGroup({ watchlistGroupId: watchListGroupId }, getResourceHeaderWithToken());
      setWatchListGroups(curr => mergeObjectInto({ ...curr }, { watchListGroups: newWatchListGroups }));
    },
    [watchListGroups, setWatchListGroups, deleteGroup, getResourceHeaderWithToken],
  );

  /**
   * Deletes ALL WatchListInstruments from a WatchList group
   * @param editedWatchListGroup
   */
  const deleteAllWatchListInstrumentFromGroup = useCallback(
    async (editedWatchListGroup: WatchListGroup) => {
      const newWatchListGroups = watchListGroups.watchListGroups.map(group =>
        group.id !== editedWatchListGroup.id ? group : { ...editedWatchListGroup, items: [] },
      );
      // UI trick, first save the new list, and then update the local state, so during loading it will look better
      await updateWatchListGroup(
        mapWatchListGroupToWatchListGroupRequest({
          ...editedWatchListGroup,
          items: [],
        }),

        getResourceHeaderWithToken(),
      );
      setWatchListGroups(curr => mergeObjectInto({ ...curr }, { watchListGroups: newWatchListGroups }));
    },
    [watchListGroups, setWatchListGroups, updateWatchListGroup, getResourceHeaderWithToken],
  );

  /**
   * Deletes ONE WatchListInstrument from a WatchList group
   * @param editedWatchListGroup
   * @param watchListInstrument
   */
  const deleteWatchListInstrumentFromGroup = useCallback(
    async (editedWatchListGroup: WatchListGroup, watchListInstrument: WatchListInstrumentFull): Promise<void> => {
      const newWatchListGroup: WatchListGroup = {
        ...editedWatchListGroup,
        items: editedWatchListGroup.items.filter(item => item.instrument.ric !== watchListInstrument.instrument.ric),
      };
      const newWatchListGroups = watchListGroups.watchListGroups.map(group =>
        group.id === editedWatchListGroup.id ? newWatchListGroup : group,
      );
      // if the group has an ID we need to persist
      if (editedWatchListGroup.id) {
        await updateWatchListGroup(
          mapWatchListGroupToWatchListGroupRequest(newWatchListGroup),
          getResourceHeaderWithToken(),
        );
      }
      setWatchListGroups(curr => mergeObjectInto({ ...curr }, { watchListGroups: newWatchListGroups }));
    },
    [watchListGroups, setWatchListGroups, updateWatchListGroup, getResourceHeaderWithToken],
  );

  /**
   * Reorders the watchlist groups
   * @param newWatchListGroupIds
   */
  const reorderWatchListGroupsByWatchListGroupIds = useCallback(
    async (newWatchListGroupIds: Array<WatchListGroupId>) => {
      const newWatchListGroups = newWatchListGroupIds.map(
        groupId => watchListGroups.watchListGroups.find(group => group.id === groupId.id) as WatchListGroup,
      );
      setWatchListGroups(curr => mergeObjectInto({ ...curr }, { watchListGroups: newWatchListGroups }));
      await updateWatchListGroupOrder(
        {
          requestBody: newWatchListGroupIds.map(group => group.id),
        },
        getResourceHeaderWithToken(),
      );
    },
    [watchListGroups, setWatchListGroups, updateWatchListGroupOrder, getResourceHeaderWithToken],
  );

  /**
   * Adds a new instrument to a watchlist group
   * @param editedWatchListGroup
   * @param newInstrument
   */
  const addInstrumentToWatchList = useCallback(
    (editedWatchListGroup: EditedWatchlistGroup, newInstrument: ListedInstrument): EditedWatchlistGroup | never => {
      if (editedWatchListGroup.items.some(item => item.instrument.ric === newInstrument.ric)) {
        throw new Error(translation.watchList.editor.itemIsCreated);
      }

      const newItem: WatchListInstrumentFull = {
        id: newInstrument.ric,
        instrument: newInstrument,
      };

      return {
        ...editedWatchListGroup,
        items: [...editedWatchListGroup.items, newItem],
      };
    },
    [translation],
  );

  /**
   * Register all instruments from a watchlist group to update from EStream changes
   * @param selectedGroup the selected watchlist group
   * @returns a function to unregister the EStream listeners
   */
  const registerEStreamWatchListUpdates = useCallback(
    (selectedGroup: number | undefined, ref: NbDataGridRef | null): (() => void) => {
      const watchListGroup = store
        .get(watchListGroupsAtom)
        .watchListGroups.find(watchListItem => watchListItem.id === selectedGroup);
      if (!watchListGroup || !selectedGroup) {
        return () => {};
      }
      const instruments = watchListGroup.items.map(watchListInstrument => watchListInstrument.instrument);

      return subscribeToInstrumentChanges(
        {
          instruments,
          source: 'watchlist',
          dataScope: [InstrumentSubscriptionDataScope.MarketData, InstrumentSubscriptionDataScope.Book10],
          forceRequestStaticData: true,
        },
        items => {
          const apiRef = ref?.$getGridApiRef();
          if (!apiRef?.current) {
            logger.error(`Failed to update watchlist, because apiRef is not yet set`);
            return;
          }
          const changes: Array<WatchListInstrumentFull> = [];
          items.forEach(item => {
            const model = apiRef.current?.getRow(item.ric);
            if (model) {
              updateSingleWatchListInstrument(model, item);
              changes.push(model);
            }
          });

          if (changes.length > 0) {
            apiRef.current.updateRows(changes);
          }
        },
      );
    },
    [subscribeToInstrumentChanges, logger],
  );

  const loadDefaults = useCallback(async () => {
    // if we already have defaults, we don't need to load them again
    if (defaults.defaults.length > 0) {
      return;
    }

    try {
      setDefaults({ isLoading: true, error: false, defaults: [] });
      const response = await getWatchlistDefaults(getResourceHeaderWithToken());
      setDefaults({ isLoading: false, error: false, defaults: response.data });
    } catch (err) {
      setDefaults({ isLoading: false, error: true, defaults: [] });
    }
  }, [getResourceHeaderWithToken, getWatchlistDefaults, defaults, setDefaults]);

  const getDefaultGroup = useCallback(
    async (groupId: number) => {
      const response = await getWatchlistDefaultGroup({ watchlistGroupId: groupId }, getResourceHeaderWithToken());
      return response.data;
    },
    [getWatchlistDefaultGroup, getResourceHeaderWithToken],
  );

  return {
    watchListGroups: watchListGroups.watchListGroups,
    watchListGroupsIsLoading: watchListGroups.isLoading,
    watchListGroupsError: watchListGroups.error,
    loadWatchListGroups,
    createOrUpdateWatchListGroup,
    deleteAllWatchListInstrumentFromGroup,
    deleteWatchListInstrumentFromGroup,
    deleteWatchListGroup,
    reorderWatchListGroupsByWatchListGroupIds,
    addInstrumentToWatchList,
    registerEStreamWatchListUpdates,
    loadDefaults,
    defaults,
    getDefaultGroup,
    mapWatchListGroupTabToWatchListGroup,
  };
};
